import React, { useState, useEffect } from 'react';
import { useReport } from '../context/ReportContext';

const ReportForm = ({ onSubmitSuccess }) => {
  const [yesterdayText, setYesterdayText] = useState('');
  const [todayText, setTodayText] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { currentReport, loading, submitReport } = useReport();

  // Load existing report data
  useEffect(() => {
    if (currentReport && currentReport.status !== 'no_report') {
      setYesterdayText(currentReport.yesterdayText || '');
      setTodayText(currentReport.todayText || '');
    }
  }, [currentReport]);

  const isEditable = !currentReport || currentReport.status !== 'locked';
  const charCountYesterday = yesterdayText.length;
  const charCountToday = todayText.length;
  const maxChars = 1000;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess('');

    // Validation
    if (!yesterdayText.trim() && !todayText.trim()) {
      setSubmitError('Please fill in at least one section (Yesterday or Today)');
      return;
    }

    if (yesterdayText.length > maxChars) {
      setSubmitError(`Yesterday section exceeds ${maxChars} characters`);
      return;
    }

    if (todayText.length > maxChars) {
      setSubmitError(`Today section exceeds ${maxChars} characters`);
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitReport(yesterdayText, todayText, false);

      // Normal submission flow
      setSubmitSuccess('✅ Report submitted successfully!');
      
      if (result.teamComplete) {
        setSubmitSuccess('✅ Report submitted! Your team is now complete and reports are locked.');
      }

      // Call parent callback
      if (onSubmitSuccess) {
        onSubmitSuccess(result);
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSubmitSuccess(''), 3000);
    } catch (error) {
      setSubmitError(error.response?.data?.error || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading report...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Error Message */}
      {submitError && (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {submitError}
        </div>
      )}

      {/* Success Message */}
      {submitSuccess && (
        <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded">
          {submitSuccess}
        </div>
      )}

      {/* Locked Warning */}
      {currentReport && currentReport.status === 'locked' && (
        <div className="p-4 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded">
          ⚠️ This report has been locked and cannot be edited. Your team's summary has been submitted.
        </div>
      )}

      {/* Yesterday Section */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Yesterday
          <span className="text-gray-500 ml-2 text-xs">
            ({charCountYesterday}/{maxChars})
          </span>
        </label>
        <textarea
          value={yesterdayText}
          onChange={(e) => setYesterdayText(e.target.value)}
          disabled={!isEditable}
          placeholder="What did you do yesterday? (List items or free text)"
          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 resize-none ${
            !isEditable ? 'bg-gray-100 cursor-not-allowed' : ''
          } ${charCountYesterday > maxChars ? 'border-red-500' : ''}`}
          rows="4"
          maxLength={maxChars}
        />
        {charCountYesterday > maxChars && (
          <p className="text-red-500 text-xs mt-1">Exceeds character limit</p>
        )}
      </div>

      {/* Today Section */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Today
          <span className="text-gray-500 ml-2 text-xs">
            ({charCountToday}/{maxChars})
          </span>
        </label>
        <textarea
          value={todayText}
          onChange={(e) => setTodayText(e.target.value)}
          disabled={!isEditable}
          placeholder="What will you do today? (List items or free text)"
          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 resize-none ${
            !isEditable ? 'bg-gray-100 cursor-not-allowed' : ''
          } ${charCountToday > maxChars ? 'border-red-500' : ''}`}
          rows="4"
          maxLength={maxChars}
        />
        {charCountToday > maxChars && (
          <p className="text-red-500 text-xs mt-1">Exceeds character limit</p>
        )}
      </div>

      {/* Format Hint */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
        <p className="font-semibold mb-1">💡 Formatting Tip:</p>
        <p>You can use bullet points or free text. Example:</p>
        <pre className="bg-white p-2 rounded mt-2 text-xs overflow-x-auto">
- Completed project A{'\n'}- Fixed bug in module B{'\n'}- Attended meeting
        </pre>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={!isEditable || submitting}
        className="w-full bg-blue-500 text-white font-semibold py-3 rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
      >
        {submitting ? 'Submitting...' : 'Submit Report'}
      </button>

      {/* Report Status */}
      {currentReport && (
        <div className="p-3 bg-gray-50 rounded text-sm">
          <p className="text-gray-600">
            <span className="font-semibold">Status:</span>{' '}
            <span className={`inline-block px-2 py-1 rounded ${
              currentReport.status === 'submitted'
                ? 'bg-green-100 text-green-800'
                : currentReport.status === 'locked'
                ? 'bg-yellow-100 text-yellow-800'
                : currentReport.status === 'draft'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-800'
            }`}>
              {currentReport.status.toUpperCase()}
            </span>
          </p>
          {currentReport.submittedAt && (
            <p className="text-gray-600 mt-1">
              <span className="font-semibold">Submitted:</span>{' '}
              {new Date(currentReport.submittedAt).toLocaleString()}
            </p>
          )}
        </div>
      )}

    </form>
  );
};

export default ReportForm;
